import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { init, createMarkers } from './methods';
import { routers } from 'settings';
import { withRouter } from 'react-router-dom';
import i18next from 'i18next';
import s from './style.module.scss';
import { toast } from 'react-toastify';

const internals = {};

const ConcentratorsMap = function (props) {
	const { data, headings, onTimeChange, setPatientId, history } = props;
	// Hooks
	const mapNode = useRef(null);
	const [timeValue, setTimeValue] = useState(43200);

	// Memoized marker click handler
	const handleMarkerClick = useCallback(
		(markerData) => {
			const patientId =
				markerData && markerData.patientId ? markerData.patientId : null;
			// Extract cp_id from itemId if available
			const cp_id =
				Array.isArray(markerData?.itemId) && markerData.itemId.length > 0
					? markerData.itemId[0]
					: null;
			if (patientId) {
				if (typeof setPatientId === 'function') {
					setPatientId({ patientId, errorId: null, cp_id: cp_id });
				}
				if (history && typeof history.push === 'function') {
					history.push(routers.patientDetail);
				} else {
					console.warn(
						'history.push is not a function or history is missing:',
						history,
					);
				}
			} else {
				toast.warn('No Active Patient Found for this marker');
				console.error('No patient id found for this marker:', markerData);
			}
		},
		[setPatientId, history],
	);

	// Effects
	useEffect(() => {
		internals.map = init({ node: mapNode.current });
	}, []);
	useEffect(() => {
		(async () => {
			try {
				await internals.map;
				await createMarkers({
					data,
					headings,
					onClick: handleMarkerClick,
					onHover: (markerData, event) => {
						setHoveredMarker(markerData);
						setHoverPosition({ x: event?.clientX, y: event?.clientY });
					},
					onMouseOut: () => {
						setHoveredMarker(null);
						setHoverPosition(null);
					},
				});
			} catch (err) {
				console.error('Failed to initialize map or markers:', err);
				toast.error(i18next.t('Failed_to_load_map_Please_try_reloading'));
			}
		})();
	}, [data, headings, handleMarkerClick]);

	const handleTimeChange = (e) => {
		const value = Number(e.target.value);
		setTimeValue(value);
		if (onTimeChange) {
			onTimeChange(value);
		}
	};

	const [hoveredMarker, setHoveredMarker] = useState(null);
	const [hoverPosition, setHoverPosition] = useState(null);

	return (
		<div className={s.map}>
			<div className={s.mapFilters}>
				<select
					value={timeValue}
					onChange={handleTimeChange}
					className={s.selectWithIcon}
					style={{
						padding: '0.5rem',
						borderRadius: '4px',
						border: '1px solid #ccc',
					}}
				>
					<option value={525600}>{i18next.t('Last_1_year')}</option>
					<option value={43200}>{i18next.t('Last_1_month')}</option>
					<option value={10080}>{i18next.t('Last_1_week')}</option>
					<option value={1440}>{i18next.t('Last_24_hours')}</option>
				</select>
				<div
					className={s.legend}
					style={{
						marginTop: '1rem',
						padding: '0.5rem 0',
						borderTop: '1px solid #eee',
						fontSize: '1.1em',
					}}
				>
					<div className={s.legendRow} style={{ display: 'block' }}>
						<div
							className={s.legendItem}
							style={{
								marginBottom: '0.5em',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<span className={s.redDot}></span>{' '}
							<span style={{ fontWeight: 'bold' }}>
								{i18next.t('High_Severity_Error')}
							</span>
						</div>
						<div
							className={s.legendItem}
							style={{
								marginBottom: '0.5em',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<span className={s.orangeDot}></span>{' '}
							<span style={{ fontWeight: 'bold' }}>
								{i18next.t('Low_Severity_Error')}
							</span>
						</div>
						<div
							className={s.legendItem}
							style={{ display: 'flex', alignItems: 'center' }}
						>
							<span className={s.greenDot}></span>{' '}
							<span style={{ fontWeight: 'bold' }}>
								{i18next.t('No_Error')}
							</span>
						</div>
					</div>
				</div>
			</div>
			<div ref={mapNode} />
			{hoveredMarker && hoverPosition && (
				<div
					style={{
						position: 'fixed',
						top: hoverPosition.y + 10,
						left: hoverPosition.x + 10,
						background: 'white',
						border: '1px solid #bbb',
						borderRadius: '10px',
						padding: '10px 16px',
						boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
						zIndex: 1000,
						width: 'auto',
						height: 'auto',
						wordBreak: 'break-word',
						fontFamily: 'monospace',
						maxWidth: '90vw',
						maxHeight: '70vh',
						overflow: 'auto',
					}}
				>
					<div
						style={{
							fontSize: '15px',
							fontWeight: 600,
							marginBottom: '5px',
							color: '#333',
						}}
					>
						Device Details
					</div>
					<hr
						style={{
							border: 'none',
							borderTop: '1.5px solid #e0e0e0',
							margin: '0 0 5px 0',
						}}
					/>
					<div style={{ fontSize: '14px', marginBottom: '6px' }}>
						<span style={{ fontWeight: 500, color: '#666' }}>Model Name:</span>{' '}
						<span style={{ fontWeight: 600, color: '#222' }}>
							{hoveredMarker.modelName}
						</span>
					</div>
					<div style={{ fontSize: '14px', marginBottom: '6px' }}>
						<span style={{ fontWeight: 500, color: '#666' }}>
							Serial Number:
						</span>{' '}
						<span style={{ fontWeight: 600, color: '#222' }}>
							{hoveredMarker.serialNumber}
						</span>
					</div>
					<div style={{ fontSize: '14px', marginBottom: '6px' }}>
						<span style={{ fontWeight: 500, color: '#666' }}>Error Name:</span>{' '}
						<span
							style={{
								fontWeight: 600,
								color:
									hoveredMarker.errorSeverity === 'high'
										? '#d32f2f'
										: hoveredMarker.errorSeverity === 'medium' ||
										    hoveredMarker.errorSeverity === 'low'
										  ? '#ff9800'
										  : '#388e3c',
							}}
						>
							{hoveredMarker.errorName || 'No Error'}
						</span>
					</div>
					<div style={{ fontSize: '14px' }}>
						<span style={{ fontWeight: 500, color: '#666' }}>
							Last Connected:
						</span>{' '}
						<span style={{ fontWeight: 600, color: '#222' }}>
							{hoveredMarker.lastConnected
								? new Date(hoveredMarker.lastConnected).toLocaleString(
										undefined,
										{
											year: 'numeric',
											month: 'short',
											day: 'numeric',
											hour: '2-digit',
											minute: '2-digit',
										},
								  )
								: '-'}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

ConcentratorsMap.defaultProps = {
	data: [],
	headings: [],
	onClick: () => {},
	onTimeChange: () => {},
	setPatientId: () => {},
	history: { push: () => {} },
};

ConcentratorsMap.propTypes = {
	data: PropTypes.array,
	headings: PropTypes.array,
	onClick: PropTypes.func,
	onTimeChange: PropTypes.func,
	setPatientId: PropTypes.func,
	history: PropTypes.object,
};

export default withRouter(ConcentratorsMap);
